import createClient from "openapi-fetch";

import type { paths } from "@/types/openapi.interfaces";

const client = createClient<paths>({
  referrerPolicy: "origin-when-cross-origin",
});

/**
 * Retrieve the CSRF token initially set by the server. Stored as an unsafe,
 * non-HTTPS cookie as `csrftoken` token. Extract the token from the cookie and return it.
 */
export function getCsrfToken() {
  const csrfToken = document.cookie.split("; ").find((row) => row.startsWith("csrftoken"));

  if (!csrfToken) {
    return "";
  }

  return csrfToken.split("=")[1];
}

function getDefaultHeaders() {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-CSRFToken": getCsrfToken(),
  };
}

export async function getTranslations(languageId?: string) {
  return client.GET("/api/translations/", {
    params: { query: { language: languageId, limit: 1000 } },
  });
}

export async function postLogin({ token }: { token: string }) {
  return client.POST("/api/users/me", {
    headers: { ...getDefaultHeaders() },
    body: { token } as unknown as any,
  });
}

export async function postUserMe() {
  return client.POST("/api/users/me", {
    headers: { ...getDefaultHeaders() },
  });
}

export async function getRelog(uid: string) {
  return client.GET("/api/users/relog", {
    headers: { ...getDefaultHeaders() },
    params: { query: { uid } },
  });
}

export async function getProducts() {
  return client.GET("/api/products", {
    headers: { ...getDefaultHeaders() },
  });
}

export async function getDotations() {
  return client.GET("/api/dotations", {
    headers: { ...getDefaultHeaders() },
  });
}

export async function getReceipts() {
  return client.GET("/api/receipts", {
    headers: { ...getDefaultHeaders() },
  });
}

export async function getReceipt(id: string) {
  return client.GET(`/api/receipts/{id}`, {
    headers: { ...getDefaultHeaders() },
    params: { path: { id } },
  });
}

export async function postReceipt(files: File[]) {
  const formData = new FormData();

  files.forEach((file) => formData.append("files", file));

  return client.POST("/api/receipts", {
    headers: {
      Accept: "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: formData as unknown as any,
  });
}

export async function postClaimFreeText(id: string, value: string) {
  return client.POST("/api/receipts/{id}/claim-free-text", {
    headers: { ...getDefaultHeaders() },
    params: {
      path: { id },
    },
    body: { text: value },
  });
}

export async function postClaimMissingPurchase(id: string, value: string) {
  return client.POST("/api/receipts/{id}/claim-on-missing-purchase", {
    headers: { ...getDefaultHeaders() },
    params: {
      path: { id },
    },
    body: { ean: value },
  });
}

export async function postClaimPurchase(id: string, lineId: string) {
  return client.POST("/api/receipts/{id}/claim-on-purchase-without-ean", {
    headers: { ...getDefaultHeaders() },
    params: {
      path: { id },
    },
    body: { line_id: lineId },
  });
}

export async function postConfirmClaims(id: string) {
  return client.POST("/api/receipts/{id}/confirm-claims", {
    headers: { ...getDefaultHeaders() },
    params: {
      path: { id },
    },
  });
}

export async function getDiscount() {
  return client.GET("/api/users/me/discount", {
    headers: { ...getDefaultHeaders() },
  });
}

export async function getLanguage() {
  return client.GET("/api/languages/", {
    headers: { ...getDefaultHeaders() },
  });
}
